/*
 * Co-located server-data hooks for the product-config + credentials surfaces
 * (U23). These live here (not in the shared `src/api/queries.ts`) per the unit
 * boundary; they call the typed `api` client directly and stay fully typed off
 * `schema.ts` — no `any` crosses the boundary.
 *
 * Query keys are namespaced under "products" / "credentials" so a write
 * invalidates exactly its own list and any open detail.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { api, unwrap, ApiError } from "../../api/client";
import type { Schemas } from "../../api/client";
import type { paths } from "../../api/schema";

export type ProductConfigResponse = Schemas["ProductConfigResponse"];
export type ProductConfigUpsert = Schemas["ProductConfigUpsert"];
export type CredentialResponse = Schemas["CredentialResponse"];
export type CredentialUpsert = Schemas["CredentialUpsert"];
export type CredentialRotate = Schemas["CredentialRotate"];

/**
 * One credential-kind descriptor from `GET /credentials/kinds` — the per-kind
 * field registry that drives the credential form. Derived from the generated
 * (inline-typed) response so it tracks the backend contract; no hand-written
 * field list lives in the SPA anymore.
 */
export type CredentialKindDescriptor =
  paths["/credentials/kinds"]["get"]["responses"][200]["content"]["application/json"][number];
export type CredentialFieldDescriptor = CredentialKindDescriptor["fields"][number];

export const configKeys = {
  products: ["products"] as const,
  credentials: ["credentials"] as const,
} as const;

/* ---- Products ----------------------------------------------------------- */

export function useProducts(
  options?: Partial<UseQueryOptions<ProductConfigResponse[]>>,
) {
  return useQuery({
    queryKey: configKeys.products,
    queryFn: async () => unwrap(await api.GET("/products")),
    ...options,
  });
}

/**
 * Create or update a product config.
 *
 *   - CREATE (no `originalKey`): POST /products with the body's key. The key must be
 *     globally unique across brands — a collision is a 409 the form surfaces.
 *   - UPDATE / RENAME (`originalKey` given): PUT /products/{originalKey}. The body's
 *     `key` may differ from `originalKey` to rename the product (same cross-brand
 *     uniqueness). A config-only save sends the same key.
 */
export function useUpsertProduct() {
  const qc = useQueryClient();
  return useMutation<
    ProductConfigResponse,
    unknown,
    { originalKey: string | null; body: ProductConfigUpsert }
  >({
    mutationFn: async ({ originalKey, body }) =>
      originalKey
        ? unwrap(
            await api.PUT("/products/{key}", {
              params: { path: { key: originalKey } },
              body,
            }),
          )
        : unwrap(await api.POST("/products", { body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: configKeys.products });
    },
  });
}

/* ---- Credentials ------------------------------------------------------ */

export function useCredentials(
  options?: Partial<UseQueryOptions<CredentialResponse[]>>,
) {
  return useQuery({
    queryKey: configKeys.credentials,
    queryFn: async () => unwrap(await api.GET("/credentials")),
    ...options,
  });
}

/**
 * The credential-kind registry (`GET /credentials/kinds`). Viewer-readable;
 * drives the form's per-kind field rendering. Cached under
 * `["credentials","kinds"]` (static-ish metadata — no write invalidates it).
 */
export function useCredentialKinds(
  options?: Partial<UseQueryOptions<CredentialKindDescriptor[]>>,
) {
  return useQuery({
    queryKey: ["credentials", "kinds"] as const,
    queryFn: async () => unwrap(await api.GET("/credentials/kinds")),
    ...options,
  });
}

/**
 * The PUBLIC (non-secret) field values for a credential
 * (`GET /credentials/{kind}/fields`) — used to pre-fill the non-secret fields
 * when rotating. `enabled` gates the fetch to when a rotate form is open. A 404
 * (credential absent) is NOT retried and must NOT throw the page; the caller
 * treats any failure as "no prefill" (the query's `error`/`data` is read
 * directly, never surfaced as a fatal state).
 */
export function useCredentialPublicFields(kind: string, enabled: boolean) {
  return useQuery<Record<string, string>>({
    queryKey: ["credentials", "fields", kind] as const,
    queryFn: async () =>
      unwrap(
        await api.GET("/credentials/{kind}/fields", {
          params: { path: { kind } },
        }),
      ),
    enabled: enabled && kind.length > 0,
    retry: false,
  });
}

interface CredentialUpsertVars {
  kind: string;
  body: CredentialUpsert;
}

/** Create-or-replace a credential (admin). Secret is write-only. */
export function useUpsertCredential() {
  const qc = useQueryClient();
  return useMutation<CredentialResponse, unknown, CredentialUpsertVars>({
    mutationFn: async ({ kind, body }) =>
      unwrap(
        await api.PUT("/credentials/{kind}", {
          params: { path: { kind } },
          body,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: configKeys.credentials });
    },
  });
}

interface CredentialRotateVars {
  kind: string;
  body: CredentialRotate;
}

/** Rotate an existing credential's secret (admin). 404 if it doesn't exist. */
export function useRotateCredential() {
  const qc = useQueryClient();
  return useMutation<CredentialResponse, unknown, CredentialRotateVars>({
    mutationFn: async ({ kind, body }) =>
      unwrap(
        await api.POST("/credentials/{kind}/rotate", {
          params: { path: { kind } },
          body,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: configKeys.credentials });
    },
  });
}

/** Delete a credential (admin). */
export function useDeleteCredential() {
  const qc = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (kind) => {
      const { error, response } = await api.DELETE("/credentials/{kind}", {
        params: { path: { kind } },
      });
      // 204 No Content: openapi-fetch returns no data; only throw on error.
      if (error !== undefined) {
        throw new ApiError(response.status, error);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: configKeys.credentials });
    },
  });
}
